// src/pages/public/ServicesPage.jsx - ANIMATED VERSION (matches HomePage style)
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import ServiceDetailPage from './ServiceDetailPage';
import Loading from '../../components/common/Loading';
import Pagination from '../../components/common/Pagination';
import { serviceService } from '../../services/service';
import { Play, Phone, ArrowRight, Search, ChevronDown, MessageCircle, Star, Filter, X, SlidersHorizontal } from 'lucide-react';

/* ── Color tokens (sama seperti HomePage) ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
};

/* ── useInView hook ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(50px)',
    left:  'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.88)',
    fade:  'translateY(0px)',
  };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : from[dir],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── CountUp ── */
const CountUp = ({ end, suffix = '' }) => {
  const [n, setN] = useState(0);
  const [ref, inView] = useInView();
  const done = useRef(false);
  useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / 2000, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView]);
  return <span ref={ref}>{n}{suffix}</span>;
};

/* ── Floating Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.3,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── SectionLabel ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 18 }}>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 12, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ── BtnPrimary ── */
const BtnPrimary = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '13px 26px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberHover : C.amber,
        color: C.white, fontWeight: 600, fontSize: 15,
        letterSpacing: '0.02em', fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 14px 32px rgba(217,119,6,0.48)' : '0 4px 16px rgba(217,119,6,0.32)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── BtnOutline ── */
const BtnOutline = ({ onClick, children, dark = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  const borderColor = dark ? 'rgba(255,255,255,0.5)' : C.amber;
  const textColor   = dark ? C.white : C.amber;
  const bgHov       = dark ? 'rgba(255,255,255,0.1)' : 'rgba(217,119,6,0.07)';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 26px', borderRadius: 50, cursor: 'pointer',
        backgroundColor: hov ? bgHov : 'transparent',
        color: textColor,
        border: `2px solid ${borderColor}`,
        fontWeight: 600, fontSize: 15,
        letterSpacing: '0.02em', fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── Service Type Pill ── */
const TypePill = ({ active, onClick, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '9px 20px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: active ? C.amber : (hov ? 'rgba(217,119,6,0.07)' : C.white),
        color: active ? C.white : (hov ? C.amber : C.gray600),
        fontWeight: 600, fontSize: 13.5,
        fontFamily: 'inherit',
        border: `1.5px solid ${active ? C.amber : (hov ? C.amber : C.gray200)}`,
        boxShadow: active ? '0 4px 14px rgba(217,119,6,0.38)' : '0 1px 4px rgba(0,0,0,0.06)',
        transform: hov && !active ? 'translateY(-2px)' : active ? 'translateY(-1px)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const ServicesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const videoRef = useRef(null);

  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    search: searchParams.get('search') || '',
    featured: searchParams.get('featured') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || ''
  });

  const [services, setServices]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showFilters, setShowFilters]     = useState(false);
  const [sortBy, setSortBy]               = useState('created_at');
  const [sortOrder, setSortOrder]         = useState('desc');
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [hoveredCard, setHoveredCard]     = useState(null);
  const [scrollY, setScrollY]             = useState(0);
  const [mousePos, setMousePos]           = useState({ x: 0, y: 0 });
  const [searchInput, setSearchInput]     = useState(filters.search);
  const itemsPerPage = 12;

  const serviceTypes = [
    { id: '', name: 'All Services', emoji: '✨' },
    { id: 'transport',  name: 'Transportation', emoji: '🚗' },
    { id: 'tour',       name: 'Tours & Activities', emoji: '🗺️' },
    { id: 'equipment',  name: 'Equipment Rental', emoji: '⚙️' },
    { id: 'beauty',     name: 'Beauty Services', emoji: '💄' },
    { id: 'spa',        name: 'Spa & Wellness', emoji: '🧘' },
    { id: 'sport',      name: 'Sports & Fitness', emoji: '⚽' },
    { id: 'other',      name: 'Other Services', emoji: '🎯' },
  ];

  const serviceTypeMap = {
    transport: 'Transportation', tour: 'Tour & Travel',
    equipment: 'Equipment Rental', beauty: 'Beauty Services',
    spa: 'Spa & Wellness', sport: 'Sports & Activities', other: 'Other Services'
  };

  const serviceEmojiMap = {
    transport: '🚗', tour: '🗺️', equipment: '⚙️',
    beauty: '💄', spa: '🧘', sport: '⚽', other: '🎯'
  };

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: itemsPerPage, status: 'active' };
      if (filters.type)     params.type     = filters.type;
      if (filters.search)   params.search   = filters.search;
      if (filters.featured === 'true') params.featured = 'true';
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;
      if (sortBy)    params.sortBy    = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const response = await serviceService.getAll(params);
      if (response?.success) {
        const d = response.data?.services || response.data || [];
        setServices(Array.isArray(d) ? d : []);
        setTotalServices(response.total || d.length || 0);
        setTotalPages(response.totalPages || Math.ceil((response.total || 0) / itemsPerPage));
      } else {
        setServices([]); setTotalServices(0); setTotalPages(1);
      }
      setError(null);
    } catch (err) {
      setError(err.message); setServices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, [filters, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.type)               params.set('type', filters.type);
    if (filters.search)             params.set('search', filters.search);
    if (filters.featured === 'true') params.set('featured', 'true');
    if (filters.minPrice)           params.set('minPrice', filters.minPrice);
    if (filters.maxPrice)           params.set('maxPrice', filters.maxPrice);
    if (sortBy !== 'created_at')    params.set('sortBy', sortBy);
    if (sortOrder !== 'desc')       params.set('sortOrder', sortOrder);
    setSearchParams(params);
    setCurrentPage(1);
  }, [filters, sortBy, sortOrder]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters({ type: '', search: '', featured: '', minPrice: '', maxPrice: '' });
    setSearchInput('');
    setSortBy('created_at'); setSortOrder('desc');
  };
  const getActiveFiltersCount = () => Object.values(filters).filter(v => v && v !== '').length;

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const heroOpacity  = Math.max(0, 1 - scrollY / 500);
  const heroParallax = scrollY * 0.35;

  return (
    <MainLayout>
      {/* ── Global CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb {
          0%   { transform: translateY(0px)   scale(1);   }
          100% { transform: translateY(-40px) scale(1.1); }
        }
        @keyframes heroIn {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0   rgba(217,119,6,0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(217,119,6,0);   }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0px);  opacity: 1;   }
          50%      { transform: translateY(10px); opacity: 0.4; }
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to   { opacity: 1; transform: translateX(0);     }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes ripple {
          0%   { transform: scale(0.95); opacity: 1;   }
          100% { transform: scale(2.5);  opacity: 0;   }
        }

        .svc-hero-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
        .svc-hero-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .svc-hero-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.7s both; }
        .svc-hero-4 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.9s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        /* ── Service Cards ── */
        .svc-card {
          transition: transform 0.55s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.4s ease,
                      border-color 0.3s ease;
          cursor: pointer;
        }
        .svc-card:hover {
          transform: translateY(-16px) scale(1.015) !important;
          box-shadow: 0 40px 80px rgba(0,0,0,0.15) !important;
          border-color: rgba(217,119,6,0.3) !important;
        }
        .svc-card-img {
          transition: transform 0.8s cubic-bezier(0.16,1,0.3,1);
        }
        .svc-card:hover .svc-card-img {
          transform: scale(1.12);
        }
        .svc-card-overlay {
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        .svc-card:hover .svc-card-overlay {
          opacity: 1;
        }
        .svc-card-action {
          opacity: 0;
          transform: translateY(16px);
          transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
        }
        .svc-card:hover .svc-card-action {
          opacity: 1;
          transform: translateY(0);
        }
        .svc-card-badge {
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .svc-card:hover .svc-card-badge {
          transform: scale(1.1) rotate(3deg);
        }

        /* ── Filter sidebar ── */
        .filter-row {
          transition: background 0.2s ease, transform 0.2s ease;
          border-radius: 10px; padding: 8px 12px; margin: -4px -12px;
          cursor: pointer;
        }
        .filter-row:hover { background: rgba(217,119,6,0.06); transform: translateX(6px); }

        /* ── Type pills hover ── */
        .type-scroll::-webkit-scrollbar { height: 4px; }
        .type-scroll::-webkit-scrollbar-track { background: transparent; }
        .type-scroll::-webkit-scrollbar-thumb { background: rgba(217,119,6,0.3); border-radius: 4px; }

        /* ── Stats strip ── */
        .stat-item { transition: transform 0.3s ease; }
        .stat-item:hover { transform: translateY(-6px); }

        /* ── Sort select ── */
        .sort-select {
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .sort-select:focus {
          border-color: ${C.amber} !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.15);
          outline: none;
        }

        /* ── Featured badge pulse ── */
        .featured-pulse {
          animation: pulseDot 2s ease-in-out infinite;
        }

        /* ── Scroll bounce ── */
        .scroll-bounce { animation: bounce 2s ease-in-out infinite; }

        /* ── Cursor glow ── */
        .cursor-glow {
          pointer-events: none; position: fixed;
          width: 280px; height: 280px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }

        /* ── Filter panel animate ── */
        .filter-panel {
          transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34,1.56,0.64,1), max-height 0.5s ease;
        }

        /* ── Whatsapp button ── */
        .wa-btn {
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, background 0.3s ease;
        }
        .wa-btn:hover {
          transform: translateY(-3px) scale(1.04);
          box-shadow: 0 12px 28px rgba(22,163,74,0.4);
        }

        /* ── CTA section ── */
        .cta-card {
          transition: transform 0.4s ease;
        }
        .cta-card:hover { transform: translateY(-4px); }

        /* ── Empty state ── */
        @keyframes emptyFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-12px); }
        }
        .empty-icon { animation: emptyFloat 4s ease-in-out infinite; }

        /* ── Search input ── */
        .search-input {
          transition: box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .search-input:focus {
          box-shadow: 0 0 0 3px rgba(217,119,6,0.2);
          border-color: ${C.amber};
          outline: none;
        }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '100vh', minHeight: 600, overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img
            src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1920&q=80"
            alt="Premium Services"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.45), rgba(0,0,0,0.74))' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.2), transparent)' }} />
        <Orb size="380px" color="radial-gradient(circle, #d97706, #f59e0b)" top="6%"  left="66%" delay={0} />
        <Orb size="260px" color="radial-gradient(circle, #92400e, #d97706)" top="62%" left="-3%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '0 24px',
          transform: `translateY(${heroParallax * 0.4}px)`, opacity: heroOpacity,
        }}>
          <div className="svc-hero-1" style={{ marginBottom: 24 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 22px',
              backgroundColor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: 50,
              color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Premium Services Bali
            </span>
          </div>

          <h1 className="svc-hero-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(40px, 8.5vw, 86px)', fontWeight: 700,
            color: '#fff', lineHeight: 1.06, letterSpacing: '-0.02em', marginBottom: 18,
          }}>
            Your Perfect Bali<br />
            <span className="shimmer-text">Experience Awaits</span>
          </h1>

          <p className="svc-hero-3" style={{
            fontSize: 'clamp(15px, 2vw, 20px)', color: 'rgba(255,255,255,0.85)',
            maxWidth: 580, marginBottom: 40, fontWeight: 300, lineHeight: 1.7,
          }}>
            Discover professional services tailored for your villa and travel needs — transport, spa, tours, and more
          </p>

          {/* ── Search Bar ── */}
          <div className="svc-hero-3" style={{ width: '100%', maxWidth: 580, marginBottom: 32 }}>
            <div style={{ position: 'relative', display: 'flex', gap: 0 }}>
              <input
                type="text"
                placeholder="Search services by name or description..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleFilterChange('search', searchInput)}
                className="search-input"
                style={{
                  flex: 1, padding: '18px 22px 18px 22px',
                  borderRadius: '50px 0 0 50px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                  fontSize: 15, color: C.gray900,
                  fontFamily: 'inherit',
                }}
              />
              <button
                onClick={() => handleFilterChange('search', searchInput)}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.amberHover; e.currentTarget.style.transform = 'scale(1.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = C.amber; e.currentTarget.style.transform = 'scale(1)'; }}
                style={{
                  padding: '0 26px', borderRadius: '0 50px 50px 0',
                  backgroundColor: C.amber, color: '#fff',
                  border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                  fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                  boxShadow: '0 4px 16px rgba(217,119,6,0.4)',
                }}
              >
                <Search size={17} /> Search
              </button>
            </div>
          </div>

          <div className="svc-hero-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <BtnPrimary
              onClick={() => document.getElementById('services-grid').scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '14px 28px', fontSize: 15 }}
            >
              Explore Services <ArrowRight size={17} />
            </BtnPrimary>
            <BtnOutline
              onClick={() => window.location.href = '/contact'}
              dark
              style={{ padding: '13px 28px', fontSize: 15 }}
            >
              <Phone size={16} /> Contact Us
            </BtnOutline>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="scroll-bounce" style={{
          position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          color: 'rgba(255,255,255,0.6)',
        }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ══ STATS STRIP ══ */}
      <section style={{ padding: '56px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="200px" color="radial-gradient(circle, rgba(217,119,6,0.3), transparent)" top="-50%" left="30%" />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 28, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {[
            { end: 50, suffix: '+', label: 'Services Available' },
            { end: 98, suffix: '%', label: 'Satisfaction Rate' },
            { end: 24, suffix: '/7', label: 'Customer Support' },
            { end: 5,  suffix: '★', label: 'Average Rating' },
          ].map((s, i) => (
            <Anim key={i} delay={i * 100}>
              <div className="stat-item">
                <div className="shimmer-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 50, fontWeight: 700, marginBottom: 6 }}>
                  <CountUp end={s.end} suffix={s.suffix} />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{s.label}</div>
              </div>
            </Anim>
          ))}
        </div>
      </section>

      {/* ══ MAIN SERVICES SECTION ══ */}
      <section id="services-grid" style={{ padding: '96px 24px 80px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="420px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="0%"   left="78%" delay={2} />
        <Orb size="300px" color="radial-gradient(circle, rgba(251,191,36,0.06), transparent)" top="60%" left="-5%" delay={4} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          {/* Section header */}
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <SectionLabel text="Our Services" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Premium Services for Every Need
              </h2>
              <p style={{ fontSize: 17, color: C.gray500, maxWidth: 520, margin: '0 auto' }}>
                Everything you need for a perfect Bali stay — hand-curated and professionally managed
              </p>
            </div>
          </Anim>

          {/* ── Type Filter Pills (horizontal scroll) ── */}
          <Anim delay={80}>
            <div style={{ marginBottom: 40, position: 'relative' }}>
              <div className="type-scroll" style={{
                display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6,
                scrollbarWidth: 'thin', justifyContent: 'center', flexWrap: 'wrap',
              }}>
                {serviceTypes.map((t, i) => (
                  <TypePill
                    key={t.id}
                    active={filters.type === t.id}
                    onClick={() => handleFilterChange('type', t.id)}
                  >
                    {t.emoji} {t.name}
                  </TypePill>
                ))}
              </div>
            </div>
          </Anim>

          {/* ── Toolbar: filter toggle + sort + results count ── */}
          <Anim delay={120}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 32, flexWrap: 'wrap', gap: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.08)'; e.currentTarget.style.borderColor = C.amber; e.currentTarget.style.color = C.amber; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.gray200; e.currentTarget.style.color = C.gray600; }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 18px', borderRadius: 50, cursor: 'pointer',
                    background: C.white, border: `1.5px solid ${C.gray200}`,
                    color: C.gray600, fontWeight: 600, fontSize: 13.5,
                    fontFamily: 'inherit',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <SlidersHorizontal size={16} />
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span style={{
                      backgroundColor: C.amber, color: '#fff',
                      borderRadius: '50%', width: 20, height: 20,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>

                {getActiveFiltersCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    onMouseEnter={e => { e.currentTarget.style.color = C.amberDark; }}
                    onMouseLeave={e => { e.currentTarget.style.color = C.amber; }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: C.amber, fontWeight: 600, fontSize: 13.5,
                      fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 6,
                      transition: 'color 0.3s',
                    }}
                  >
                    <X size={14} /> Clear All
                  </button>
                )}

                {!loading && (
                  <span style={{ color: C.gray500, fontSize: 13.5 }}>
                    {totalServices} service{totalServices !== 1 ? 's' : ''} found
                  </span>
                )}
              </div>

              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={e => {
                  const [f, o] = e.target.value.split('-');
                  setSortBy(f); setSortOrder(o);
                }}
                className="sort-select"
                style={{
                  border: `1.5px solid ${C.gray200}`, borderRadius: 50,
                  padding: '10px 20px', fontSize: 13.5, fontWeight: 600,
                  background: C.white, color: C.gray700,
                  fontFamily: 'inherit', cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                }}
              >
                <option value="created_at-desc">Latest First</option>
                <option value="created_at-asc">Oldest First</option>
                <option value="price_idr-asc">Price: Low → High</option>
                <option value="price_idr-desc">Price: High → Low</option>
                <option value="title-asc">Name: A → Z</option>
                <option value="title-desc">Name: Z → A</option>
              </select>
            </div>
          </Anim>

          {/* ── Main content: sidebar + grid ── */}
          <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start' }}>

            {/* Sidebar filters */}
            {showFilters && (
              <Anim dir="left" delay={0} style={{ flexShrink: 0, width: 280 }}>
                <div style={{
                  background: C.white, borderRadius: 20,
                  boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                  border: `1px solid ${C.gray100}`,
                  padding: '28px 24px',
                  position: 'sticky', top: 24,
                }}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: C.gray900, marginBottom: 24 }}>
                    Refine Results
                  </h3>

                  {/* Price Range */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.amber, fontWeight: 700, marginBottom: 12 }}>
                      Price Range (IDR)
                    </div>
                    <input
                      type="number"
                      placeholder="Min price"
                      value={filters.minPrice}
                      onChange={e => handleFilterChange('minPrice', e.target.value)}
                      className="search-input"
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 12,
                        border: `1.5px solid ${C.gray200}`, fontSize: 13.5,
                        marginBottom: 8, fontFamily: 'inherit', color: C.gray700,
                      }}
                    />
                    <input
                      type="number"
                      placeholder="Max price"
                      value={filters.maxPrice}
                      onChange={e => handleFilterChange('maxPrice', e.target.value)}
                      className="search-input"
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 12,
                        border: `1.5px solid ${C.gray200}`, fontSize: 13.5,
                        fontFamily: 'inherit', color: C.gray700,
                      }}
                    />
                  </div>

                  {/* Featured only */}
                  <div style={{ marginBottom: 28 }}>
                    <label className="filter-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="checkbox"
                        checked={filters.featured === 'true'}
                        onChange={e => handleFilterChange('featured', e.target.checked ? 'true' : '')}
                        style={{ width: 17, height: 17, accentColor: C.amber }}
                      />
                      <span style={{ fontSize: 14, fontWeight: 600, color: C.gray700 }}>⭐ Featured Only</span>
                    </label>
                  </div>

                  <button
                    onClick={clearFilters}
                    onMouseEnter={e => { e.currentTarget.style.background = C.amberLight; e.currentTarget.style.color = C.amberDark; }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.gray100; e.currentTarget.style.color = C.gray600; }}
                    style={{
                      width: '100%', padding: '11px', borderRadius: 12, border: 'none',
                      background: C.gray100, color: C.gray600,
                      fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Reset Filters
                  </button>
                </div>
              </Anim>
            )}

            {/* Services grid */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                  <div style={{ width: 52, height: 52, border: `4px solid ${C.amberLight}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
                </div>
              ) : services.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 28, marginBottom: 52 }}>
                    {services.map((svc, i) => {
                      const emoji = serviceEmojiMap[svc.service_type] || '🎯';
                      const typeName = serviceTypeMap[svc.service_type] || 'Service';
                      return (
                        <Anim key={svc.id} delay={Math.min(i, 8) * 80}>
                          <div
                            className="svc-card"
                            onClick={() => { setSelectedService(svc); setShowModal(true); }}
                            onMouseEnter={() => setHoveredCard(svc.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            style={{
                              background: C.white, borderRadius: 20,
                              overflow: 'hidden',
                              boxShadow: '0 6px 28px rgba(0,0,0,0.08)',
                              border: `1.5px solid ${C.gray100}`,
                            }}
                          >
                            {/* Image */}
                            <div style={{ position: 'relative', height: 228, overflow: 'hidden', background: `linear-gradient(135deg, ${C.amberLight}, #fed7aa)` }}>
                              {svc.image_path ? (
                                <img
                                  className="svc-card-img"
                                  src={svc.image_path}
                                  alt={svc.title}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64 }}>
                                  {emoji}
                                </div>
                              )}
                              {/* Hover overlay */}
                              <div
                                className="svc-card-overlay"
                                style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }}
                              />
                              {/* Badges */}
                              {svc.featured === 1 || svc.featured === true ? (
                                <span style={{
                                  position: 'absolute', top: 14, left: 14,
                                  padding: '5px 13px', background: C.amber, color: '#fff',
                                  borderRadius: 50, fontSize: 11.5, fontWeight: 700,
                                  display: 'flex', alignItems: 'center', gap: 5,
                                  boxShadow: '0 4px 12px rgba(217,119,6,0.4)',
                                }}>
                                  <span className="featured-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
                                  Popular
                                </span>
                              ) : null}
                              <span
                                className="svc-card-badge"
                                style={{
                                  position: 'absolute', top: 14, right: 14,
                                  padding: '5px 12px',
                                  background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                                  borderRadius: 50, fontSize: 11.5, fontWeight: 600, color: C.gray700,
                                }}
                              >
                                {emoji} {typeName}
                              </span>
                              {/* View details hint */}
                              <div className="svc-card-action" style={{
                                position: 'absolute', bottom: 16, left: 0, right: 0,
                                display: 'flex', justifyContent: 'center',
                              }}>
                                <span style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  color: '#fff', fontWeight: 600, fontSize: 13.5,
                                  background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)',
                                  padding: '7px 18px', borderRadius: 50,
                                }}>
                                  View Details <ArrowRight size={14} />
                                </span>
                              </div>
                            </div>

                            {/* Body */}
                            <div style={{ padding: '22px 22px 20px' }}>
                              <h3 style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 21, fontWeight: 700, color: C.gray900,
                                marginBottom: 8, lineHeight: 1.25,
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}>
                                {svc.title}
                              </h3>

                              {svc.phone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.gray500, fontSize: 13, marginBottom: 8 }}>
                                  <Phone size={13} color={C.amber} /> {svc.phone}
                                </div>
                              )}

                              <p style={{
                                color: C.gray500, fontSize: 13, lineHeight: 1.7, marginBottom: 16,
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}>
                                {svc.description
                                  ? svc.description.replace(/<[^>]+>/g, '').substring(0, 100) + '...'
                                  : 'Professional service with the best quality for your perfect Bali experience.'}
                              </p>

                              <div style={{ paddingTop: 14, borderTop: `1px solid ${C.gray100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                  {svc.price_idr && svc.price_idr > 0 ? (
                                    <>
                                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.amber }}>
                                        IDR {formatCurrency(svc.price_idr)}
                                      </div>
                                      <div style={{ fontSize: 10.5, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Starting from</div>
                                    </>
                                  ) : (
                                    <div style={{ fontSize: 14, fontWeight: 600, color: C.gray600 }}>Contact for Price</div>
                                  )}
                                </div>

                                <button
                                  className="wa-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    const phone = svc.phone || '6287785686777';
                                    window.open(`https://wa.me/${phone.replace(/\D/g,'')}`, '_blank');
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '9px 16px', borderRadius: 50, border: 'none',
                                    backgroundColor: '#16a34a', color: '#fff',
                                    fontWeight: 600, fontSize: 12.5, cursor: 'pointer',
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  <MessageCircle size={13} /> WhatsApp
                                </button>
                              </div>
                            </div>
                          </div>
                        </Anim>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <Anim delay={0}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                      </div>
                    </Anim>
                  )}
                </>
              ) : (
                <Anim delay={0}>
                  <div style={{ textAlign: 'center', padding: '80px 24px', background: C.white, borderRadius: 24, boxShadow: '0 6px 28px rgba(0,0,0,0.07)' }}>
                    <div className="empty-icon" style={{ fontSize: 72, marginBottom: 20 }}>🔍</div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                      No Services Found
                    </h3>
                    <p style={{ color: C.gray500, fontSize: 15, marginBottom: 28 }}>
                      Try adjusting your filters or search term to discover more services.
                    </p>
                    <BtnPrimary onClick={clearFilters}>
                      <X size={15} /> Clear All Filters
                    </BtnPrimary>
                  </div>
                </Anim>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHY OUR SERVICES ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-8%" left="-6%" delay={3} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <SectionLabel text="Why Choose Us" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,48px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Excellence in Every Service
              </h2>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px,1fr))', gap: 16 }}>
            {[
              { emoji: '⚡', title: 'Quick Response',      desc: 'Fast confirmation within minutes, no waiting around.' },
              { emoji: '🏅', title: 'Certified Quality',   desc: 'All services verified and held to premium standards.' },
              { emoji: '💰', title: 'Best Value',          desc: 'Competitive pricing with no hidden fees.' },
              { emoji: '🌟', title: '24/7 Support',        desc: 'Our team is always available for any assistance.' },
            ].map((item, i) => (
              <Anim key={i} delay={i * 100}>
                <div
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.05)'; e.currentTarget.querySelector('.why-ico').style.transform = 'scale(1.28) rotate(-8deg)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.why-ico').style.transform = 'scale(1) rotate(0deg)'; }}
                  style={{ textAlign: 'center', padding: 32, borderRadius: 18, cursor: 'default', transition: 'background 0.35s ease' }}
                >
                  <div
                    className="why-ico"
                    style={{
                      width: 66, height: 66, backgroundColor: C.amberLight, borderRadius: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 18px', fontSize: 28,
                      transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                    }}
                  >
                    {item.emoji}
                  </div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 8 }}>{item.title}</h3>
                  <p style={{ fontSize: 13.5, color: C.gray500, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ padding: '96px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="480px" color="radial-gradient(circle, rgba(217,119,6,0.22), transparent)" top="0%"  left="46%" delay={0} />
        <Orb size="280px" color="radial-gradient(circle, rgba(251,191,36,0.12), transparent)" top="55%" left="-3%" delay={3} />

        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <p style={{ color: C.amber, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20 }}>Need Something Custom?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px,6vw,60px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 16 }}>
              Let's Build Your<br /><span className="shimmer-text">Perfect Experience</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 40, lineHeight: 1.7 }}>
              Our team is ready to assist you with free consultation and the best custom service packages.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <BtnPrimary onClick={() => window.location.href = '/contact'} style={{ padding: '15px 32px', fontSize: 16 }}>
                Contact Us <ArrowRight size={17} />
              </BtnPrimary>
              <a
                href="https://wa.me/6281529635781"
                target="_blank"
                rel="noopener noreferrer"
                className="wa-btn"
                onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 9,
                  padding: '15px 32px', borderRadius: 50, textDecoration: 'none',
                  backgroundColor: '#16a34a', color: '#fff',
                  fontWeight: 700, fontSize: 16, fontFamily: 'inherit',
                  boxShadow: '0 4px 18px rgba(22,163,74,0.38)',
                  transition: 'background 0.3s ease',
                }}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp Us
              </a>
            </div>
          </Anim>
        </div>
      </section>

      {/* ── Service Detail Modal ── */}
      {showModal && selectedService && (
        <ServiceDetailPage
          service={selectedService}
          onClose={() => { setShowModal(false); setSelectedService(null); }}
          formatCurrency={(amount) => `IDR ${formatCurrency(amount)}`}
          getServiceIcon={() => serviceEmojiMap[selectedService.service_type] || '🎯'}
          isModal={true}
        />
      )}
    </MainLayout>
  );
};

export default ServicesPage;